import styles from "./ClientDataMapping.module.css";
import React, {
  useMemo,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import {
  Row,
  Col,
  Button,
  Modal
} from "react-bootstrap";
import Select from 'react-select';
import { fetchFirmFields, fetchMappingData, updateMapping } from "./ClientDataMapping.service";
import CustomButton from "../../reusable_components/custom_button_component/Button";

import { MappingRecord, VendorFieldsResponse, FieldMappingJson } from "./ClientDataMapping.types"
import CygnusGrid from "../../../pages/reusable_components/CygnusGrid/CygnusGrid";
/**
 * Renders the client data mapping page, showing engagement letter approval data and actions.
 *
 * This component includes:
 * - Display of vendor data mapping with source of truth in a grid.
 * - Update mapping button with confirmation.
 * - Grid with updatable source of truth within the cell.
 * - Loader to indicate grid data refresh.
 *
 * @component
 * @returns {JSX.Element} The rendered approval details page with a grid, buttons
 */
const ClientDataMapping: React.FC = (props: any) => {

  const [rowData, setRowData] = useState<any[]>([])
  const [vendorFields, setVendorFields] = useState<VendorFieldsResponse | undefined>(undefined)
  const [hubspotFields, setHubspotFields] = useState<any[]>([])
  const [safesendFields, setSafesendFields] = useState<any[]>([])
  const [editedRows, setEditedRows] = useState<any>([]);
  const [fieldMappingJson, setFieldMappingJson] = useState<FieldMappingJson>({})
  const [mappingData, setMappingData] = useState<any[]>([])
  const [mappingApiData, setMappingApiData] = useState<MappingRecord[] | undefined>(undefined)
  const [clientTypeOptions, setClientTypeOptions] = useState<any[]>([])
  const [selectedType, setSelectedType] = useState(clientTypeOptions[0])
  const [updateMappingApiProgress, setUpdateMappingApiProgress] = useState(false)
  const [approveModal, setApproveModal] = useState(false)
  const [allMappingAvailable, setAllMappingAvailable] = useState(false)
  const [key, setKey] = useState<number | undefined>(undefined);
  const [pageRefresh, setPageRefresh] = useState(true);

  useEffect(() => {
    if (mappingApiData !== undefined) {
      const optionList: any[] = []
      for (let record of mappingApiData) {
        optionList.push({
          "label": record["ClientType"],
          "value": record["ClientType"],
        })
      }
      setClientTypeOptions(optionList)
      setSelectedType(optionList[0])
    }
  }, [mappingApiData])

  useEffect(() => {
    if (mappingApiData !== undefined && selectedType !== undefined) {
      let filteredMappingData: any = []

      if (selectedType !== undefined) {
        filteredMappingData = mappingApiData.filter(
          obj => Object.keys(obj).some(key => obj[key] === selectedType.value)
        )
      } else {
        filteredMappingData = mappingApiData.filter(
          obj => Object.keys(obj).some(key => obj[key] === clientTypeOptions[0].value)
        )
      }
      setMappingData(filteredMappingData[0]["MappingData"])
      const refactoredMappingData = [...filteredMappingData[0]["MappingData"]]

      for (let record of refactoredMappingData) {
        const options = []
        const vendornamemap = {
          "Hubspotname": "Hubspot CRM",
          "Safesendname": "SafeSend",
          "CCHAxcessname": "CCH Axcess Workflow"
        }
        const vendornames = ["Hubspotname", "Safesendname", "CCHAxcessname"]
        for (let [key, value] of Object.entries(record)) {
          if (vendornames.includes(key)) {
            if (value !== null) {
              options.push(vendornamemap[key as keyof typeof vendornamemap])
            } else {
              continue
            }
          } else {
            continue
          }
        }
        if (record["Source of truth"] === "") {
          if (options.length === 0) {
            record["Source of truth"] = "Cygnus MDM"
          }
        }
      }

      if (refactoredMappingData.length)
        setKey(refactoredMappingData.length);

      setRowData(refactoredMappingData)
    }
  }, [selectedType, mappingApiData])

  const transporter = (pill: any) => {
    props.transporter(pill);
  };

  useEffect(() => {

    const fetchPageData = async () => {
      setPageRefresh(true);
      await Promise.all([handleFetchMappingData(), handleFetchFirmFields()]);
      setPageRefresh(false);
    }

    fetchPageData();
  }, [])

  const handleFetchMappingData = useCallback(async (form_id?: string) => {

    try {

      const data: MappingRecord[] = await fetchMappingData(form_id);
      if (data.length > 0) {
        setMappingApiData(data)        
      }

      
    } catch (error) {
      console.log(error)
    } finally {
    }
  }, []);

  const handleFetchFirmFields = useCallback(async () => {

    try {
      const data = await fetchFirmFields();
      setVendorFields(data);
    } catch (error) {
    } finally {
    }
  }, []);

  const handleUpdateMapping = async () => {
    setUpdateMappingApiProgress(true);
    try {
      const filteredRows = rowData.filter((_, index: any) => editedRows.includes(index));
      if (filteredRows.length <= 0) {
        props.transporter("templates", {
          "config_option": { clientType: selectedType.value, targetFields: mappingData }
        })
        return;
      }

      const payload = {
        "ClientType": selectedType.value,
        "Mappings": filteredRows
      }

      const success = await updateMapping(payload);

      if (success) {
        setTimeout(() => {
          props.transporter("templates", {
            "config_option": { clientType: selectedType.value, targetFields: mappingData }
          })
          handleFetchMappingData()
          handleFetchFirmFields()
        }, 1000);
      } else {
      }
    } catch (error) {
    } finally {
      setUpdateMappingApiProgress(false);
    }
  };

  useEffect(() => {    
    const all_sot_mappings = []
    for (let [_, field] of Object.entries(fieldMappingJson)) {
      all_sot_mappings.push(field["source_of_truth"])
    }
    if (all_sot_mappings.filter(str => str && str.trim().length === 0).length > 0) {
      setAllMappingAvailable(false);
    } else {
      setAllMappingAvailable(true);
    }
  }, [fieldMappingJson])

  useEffect(() => {
    const mappingDict = {}
    if (mappingData.length > 0) {
      if (Object.keys(mappingData[0]).length > 0) {
        for (let field of mappingData) {
          Object.assign(mappingDict, {
            [field["FieldName"]]: {
              "source_of_truth": field["Source of truth"]
            }
          })
        }
        setFieldMappingJson(mappingDict)
      }
    }
  }, [mappingData])

  useEffect(() => {
    if (vendorFields && Object.keys(vendorFields).length > 0) {
      const hfo = []
      const hfv = []
      const sfo = []
      const sfv = []
      for (let field of vendorFields?.Hubspot ?? []) {
        hfo.push({
          "label": field.DisplayName,
          "value": field.APIName
        })
        hfv.push(field.APIName)
      }
      setHubspotFields(hfv)
      for (let field of vendorFields?.SafeSend ?? []) {
        sfo.push({
          "label": field.DisplayName,
          "value": field.APIName
        })
        sfv.push(field.APIName)
      }
      setSafesendFields(sfv)
    }
  }, [vendorFields])

  const gridRef: any = useRef(null);

  const gridStyle = useMemo(() => ({ height: "85vh", width: "100%" }), []);
  const [columnDefs, setColumnDefs] = useState([]);
  const [selectedRowData, setSelectedRowData] = useState({ "previewComponent": [] });

  const FieldNameRenderer = (props: any) => {
    return (
      <>{props?.data?.FieldName}</>
    )
  }

  const HubspotCellRenderer = (props: any) => {
    return (
      <>
        {props?.getValue?.() !== undefined ? props?.getValue?.() : props?.data?.["Hubspotname"]}
      </>
    )
  }
  const SafesendCellRenderer = (props: any) => {
    return (
      <>
        {props?.getValue?.() !== undefined ? props?.getValue?.() : props?.data?.["Safesendname"]}
      </>
    )
  }
  const CchAxcessCellRenderer = (props: any) => {
    return (
      <>
        {props?.getValue?.() !== undefined ? props?.getValue?.() : props?.data?.["Safesendname"]}
      </>
    )
  }
  const SourceOfTruthRenderer = (props: any) => {
    console.log(props)
    return (
      <>
        {props?.getValue?.()}
      </>
    )
  }

  const onCellValueChanged = (event: any) => {
    setEditedRows([...editedRows, event.rowIndex]);
  };
  const vendor_map = {
    "Source Of Truth": "source_of_truth"
  }
  const updateMappingDataSot = (field: any, sot: any) => {
    const index = mappingData.findIndex((obj) =>
      obj.FieldName === field)
    mappingData[index]["Source of truth"] = sot
  }

  const onCellValChanged = (params: any) => {
    if (params.oldValue !== params.newValue) {
      const fieldName = params.data["FieldName"];
      const colId = params.column.getColDef().field;

      // Construct unique key for this cell
      const editedKey = `${fieldName}::${colId}`;
      const localEdited = JSON.parse(localStorage.getItem("editedFields") || "[]");
      if (!localEdited.includes(editedKey)) {
        localEdited.push(editedKey);
        localStorage.setItem("editedFields", JSON.stringify(localEdited));
      }

      // Also update fieldMappingJson and mappingData
      const fieldMappingObject: any = fieldMappingJson?.[fieldName];
      if (fieldMappingObject) {
        fieldMappingObject[vendor_map["Source Of Truth"]] = params.newValue;
        Object.assign(fieldMappingJson[fieldName], fieldMappingObject);
        updateMappingDataSot(fieldName, params.newValue);
      }
      // Refresh the cells to apply the new style
      params.api.refreshCells({
        force: true,
        columns: [params.column.getId()],
        rowNodes: [params.node]
      });
    }
  };

  useEffect(() => {
    let colDefs: any = [{
      field: "FieldName",
      headerName: "Field",
      suppressMenu: true,
      cellRenderer: FieldNameRenderer,
      cellRendererParams: {
        selectedrow: setSelectedRowData
      },
      sizeColumnsToFit: true,
      cellStyle: { "borderRight": "0.5px solid lightgray" },
      flex: 1
    },
    {
      field: "Hubspotname",
      headerName: "Hubspot",
      cellEditor: "agSelectCellEditor",
      sizeColumnsToFit: true,
      suppressSizeToFit: true,
      cellRenderer: HubspotCellRenderer,
      onCellValueChanged: onCellValChanged,
      cellStyle: { "borderRight": "0.5px solid lightgray" },
      flex: 1
    },
    {
      field: "Safesendname",
      headerName: "SafeSend",
      suppressSizeToFit: true,
      cellEditor: "agSelectCellEditor",
      sizeColumnsToFit: true,
      cellRenderer: SafesendCellRenderer,
      onCellValueChanged: onCellValChanged,
      cellStyle: { "borderRight": "0.5px solid lightgray" },
      flex: 1
    },
    {
      field: "CCHAxcessname",
      headerName: "CCH Axcess",
      suppressSizeToFit: true,
      cellEditor: "agSelectCellEditor",
      sizeColumnsToFit: true,
      cellRenderer: CchAxcessCellRenderer,
      onCellValueChanged: onCellValChanged,
      cellStyle: { "borderRight": "0.5px solid lightgray" },
      flex: 1
    },
    {
      field: "SourceOfTruth",
      headerName: "Source Of Truth",
      cellEditor: "agSelectCellEditor",
      editable: true,
      cellEditorParams: (params: any) => {
        const options = ["Cygnus MDM"]
        const vendornamemap: any = {
          "Hubspotname": "Hubspot CRM",
          "Safesendname": "SafeSend",
          "CCHAxcessname": "CCH Axcess Workflow"
        }
        const vendornames = ["Hubspotname", "Safesendname", "CCHAxcessname"]
        for (let [key, value] of Object.entries(params["data"])) {
          if (vendornames.includes(key)) {
            if (value !== null) {
              options.push(vendornamemap[key])
            } else {
              continue
            }
          } else {
            continue
          }
        }
        return {
          values: options
        }
      },
      sizeColumnsToFit: true,
      suppressSizeToFit: true,
      cellRenderer: SourceOfTruthRenderer,
      onCellValueChanged: onCellValChanged,
      cellClassRules: {
        [styles.editedCell]: (params: any) => {
          if (!params?.data?.FieldName) return false;
          const fieldName = params.data["FieldName"];
          const editedKey = `${fieldName}::SourceOfTruth`;
          const editedFields = JSON.parse(localStorage.getItem("editedFields") || "[]");
          return editedFields.includes(editedKey);
        }
      }
    }
    ]
    setColumnDefs(colDefs)
  }, [hubspotFields, safesendFields, fieldMappingJson, rowData])

  useEffect(() => {
    return () => {
      localStorage.removeItem("editedFields");
    };
  }, []);

  const ApproveModal = (props: any) => {
    return (
      <Modal
        {...props}
        size="md"
        aria-labelledby="contained-modal-title-vcenter"
        backdropClassName="custom-backdrop-save"
        backdrop="static"
        centered
        scrollable="false"
        style={{
          maxHeight: 500,
          overflow: "auto",
          top: "20%"
        }}
        keyboard={false}
      >
        <Modal.Header closeButton>
          <h5 style={{ marginBottom: 0, marginRight: 20, marginLeft: 10 }}>Are you sure you want to save this mapping?</h5>
        </Modal.Header>
        {
          !allMappingAvailable ?
            <Modal.Body>
              <h6 style={{ marginBottom: 0, marginRight: 20, marginLeft: 10 }}>You have not completed the mapping for all the fields.</h6>
            </Modal.Body> :
            <></>
        }
        <Modal.Footer className="flex-row-space-center" style={{ width: "100%" }}>
          <Button className="custom-button" style={{
            width: "fit-content",
            display: "inline-block",
            padding: "5px 10px",
          }} onClick={() => {
            props.submit()
            props.onHide()
          }}>
            Save
          </Button>
          <Button style={{
            width: "fit-content",
            display: "inline-block",
            padding: "5px 10px",
            color: "var(--theme1_tertiary)",
            background: "transparent",
            border: 0,
            fontSize: 12
          }} onClick={() => props.onHide()}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    )
  }
  return (
    <>
      <ApproveModal
        show={approveModal}
        submit={() => handleUpdateMapping()}
        onHide={() => setApproveModal(false)}
      />
      <>
        <Row className='no-gutter page-wrapper'>
          <Col md={6} className="flex-row-space-center" style={{ padding: "0px 20px 0px 0px" }}>
            <p className="display-6" style={{ height: "fit-content", marginBottom: 0, width: "fit-content", float: "right" }}>Client Field Mapping</p> <p style={{ width: "fit-content" }}></p>
          </Col>
          <Col md={5} className="flex-row-center-end">
            <div className="form-select-wrapper" style={{ width: 300, marginLeft: 20, marginRight: 10, fontSize: 14, float: "right" }}>
              <p style={{ width: 150, fontWeight: 600, marginBottom: 0 }}>Client type:</p>
              <Select
                value={selectedType}
                onChange={(event) => setSelectedType(event)}
                options={clientTypeOptions}
                className="form-select custom-select"
                aria-label="Default"
                closeMenuOnSelect={true}
                placeholder="Select type of client"
                menuPosition="fixed"
                classNamePrefix="react-select"
              />
            </div>
          </Col>
          <Col md={1}>
            <CustomButton
              progress={updateMappingApiProgress}
              onclick={() => setApproveModal(true)}
              content={"Save"}
            />
          </Col>
        </Row>
        <Row className='no-gutter' style={{ height: "85vh" }}>
          <Col md={12}>
            <div className="ag-theme-balham" style={gridStyle}>
              <CygnusGrid
                key={key}
                fetchData={async () => {
                  // Since this is not paginated, just return everything
                  return { records: rowData, totalCount: rowData.length };
                }}
                rowModelType="clientSide"
                dataIdentifier="ClientDataMappingId"
                cachedData={rowData}
                setCachedData={setRowData}
                onLoadingChange={setPageRefresh}
                cachedIsLoading={pageRefresh}
                columnDefs={columnDefs}
                rowSelection={"multiple"}
                onCellValueChanged={(item: any) => onCellValueChanged(item)}
                ref={gridRef}
                singleClickEdit={true}
                suppressRowHoverHighlight={true}
              />
            </div>
          </Col>
        </Row>
      </>
    </>
  );
};

export default ClientDataMapping;
